import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../app/providers';
import { href, Link } from '../../app/router';
import { TEMPLATE_CATEGORIES, type Board, type TemplateCategory } from '../../lib/api';
import { Button, Dialog, Input, Select, linkClass } from '../../ui';
import { DialogClose } from '../../ui/DialogClose';

/** "Save as template" (spec §8): a template copy of the board in its project; the user stays where they are. */
export function SaveTemplateDialog({ board, onClose }: { board: Board; onClose: () => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(board.title);
  const [category, setCategory] = useState<TemplateCategory>('other');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [saved, setSaved] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || !title.trim()) return;
    setBusy(true);
    setError(false);
    try {
      await api.saveAsTemplate(board.id, { title: title.trim(), category });
      await queryClient.invalidateQueries({ queryKey: ['templates'] });
      setSaved(true);
    } catch {
      setError(true);
    }
    setBusy(false);
  };

  return (
    <Dialog title={t('boardTemplates.save')} onClose={onClose}>
      {saved ? (
        <div className="flex flex-col gap-4">
          <p role="status" className="font-sans text-sm">
            {t('boardTemplates.saved', { title: title.trim() })}
          </p>
          <div className="flex justify-end gap-2">
            <Link to={href('templates')} className={linkClass}>
              {t('boardTemplates.openGallery')}
            </Link>
            <DialogClose>
              {t('boardTemplates.close')}
            </DialogClose>
          </div>
        </div>
      ) : (
        <form className="flex flex-col gap-4" onSubmit={submit}>
          <label className="flex flex-col gap-1 font-sans text-sm font-medium text-fg">
            {t('boardTemplates.name')}
            <Input value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 font-sans text-sm font-medium text-fg">
            {t('boardTemplates.filter')}
            <Select value={category} onChange={(e) => setCategory(e.target.value as TemplateCategory)}>
              {TEMPLATE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {t(`boardTemplates.categories.${c}`)}
                </option>
              ))}
            </Select>
          </label>
          {error && (
            <p role="alert" className="font-sans text-xs text-danger">
              {t('auth.genericError')}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <DialogClose>
              {t('dashboard.cancel')}
            </DialogClose>
            <Button variant="primary" type="submit" disabled={busy || !title.trim()}>
              {t('dashboard.save')}
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
